<template>
  <div id="accounting-view">
    <div class="vx-row">
      <div class="vx-col w-full">
        <vx-card class="mb-base">
          <!-- Header -->
          <div class="flex flex-wrap items-center justify-between mb-6">
            <h3 class="mb-0">Finance</h3>
            <div class="flex items-center">
              <label class="mr-2 font-medium">Year</label>
              <vs-select v-model="selectedYear" class="year-select" @input="loadExpenses">
                <vs-select-item v-for="y in availableYears" :key="y" :value="y" :text="String(y)" />
              </vs-select>

              <vs-dropdown vs-trigger-click class="ml-3 cursor-pointer cashflow-actions">
                <vs-button type="border" color="primary" icon-pack="feather" icon="icon-menu">
                  Actions
                  <feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4 ml-1 align-middle" />
                </vs-button>
                <vs-dropdown-menu class="cashflow-actions-menu">
                  <vs-dropdown-item @click="openManualTx">
                    <div class="dd-title">Add transaction</div>
                    <div class="dd-sub">Manual cash / non-synced expense added to the month.</div>
                  </vs-dropdown-item>
                  <vs-dropdown-item @click="openBankCheck">
                    <div class="dd-title">{{ bankCheck.loading ? 'Checking…' : 'Bank link health' }}</div>
                    <div class="dd-sub">Detect accounts whose sync stopped, empty or unregistered.</div>
                  </vs-dropdown-item>
                  <vs-dropdown-item @click="openInvestments">
                    <div class="dd-title">Investment</div>
                    <div class="dd-sub">Reclassify operations as CAPEX.</div>
                  </vs-dropdown-item>
                  <vs-dropdown-item @click="exportYear">
                    <div class="dd-title">{{ exporting ? 'Exporting…' : 'Export year' }}</div>
                    <div class="dd-sub">XLSX: table à l'écran + KPI + toutes les lignes (éjectées incluses) + règles de calcul.</div>
                  </vs-dropdown-item>
                  <vs-dropdown-item @click="openRules">
                    <div class="dd-title">Eject rules</div>
                    <div class="dd-sub">Manage which operations are excluded from totals.</div>
                  </vs-dropdown-item>
                  <vs-dropdown-item @click="loadExpenses">
                    <div class="dd-title">Refresh</div>
                    <div class="dd-sub">Reload the year's figures.</div>
                  </vs-dropdown-item>
                </vs-dropdown-menu>
              </vs-dropdown>
            </div>
          </div>

          <!-- Loading -->
          <div v-if="loading" class="text-center py-12">
            <p class="text-muted">Loading expenses…</p>
          </div>

          <!-- ============ KPI SYNTHÉTIQUES (annuel) ============ -->
          <div v-if="!loading && months.length" class="kpi-panel mb-6">
            <div class="kpi-panel-title">KPI {{ selectedYear }}</div>
            <div class="kpi-grid">
              <div v-for="k in financialKpis" :key="k.label" class="kpi-cell" :class="{ 'kpi-cell-strong': k.strong }">
                <div class="kpi-label">{{ k.label }}</div>
                <div class="kpi-value" :class="k.cls">{{ k.value }}</div>
              </div>
            </div>
          </div>

          <!-- Table -->
          <vs-table v-if="!loading" :data="tableRows" stripe>
            <template slot="thead">
              <vs-th>Month</vs-th>
              <vs-th class="text-right">Chiffre d'affaire</vs-th>
              <vs-th class="text-right">Expenses EUR</vs-th>
              <vs-th class="text-right">Résultat exploitation</vs-th>
              <vs-th class="text-right">Variation Stock (EUR)</vs-th>
              <vs-th class="text-right">EBITDA Ajusté</vs-th>
              <vs-th class="text-right">m³ vendus</vs-th>
              <vs-th class="text-right">m³ sciés</vs-th>
              <vs-th class="text-right">CAPEX</vs-th>
              <vs-th class="text-right">Credit (principal)</vs-th>
              <vs-th class="text-right">Credit (dobanda)</vs-th>
              <vs-th class="text-right">Ops</vs-th>
              <vs-th class="text-right">Detail</vs-th>
            </template>
            <template slot-scope="{ data }">
              <vs-tr v-for="row in data" :key="row.month" :class="{ 'total-table-row': row.isTotal }">
                <vs-td class="font-medium">{{ row.isTotal ? ('TOTAL ' + selectedYear) : monthLabel(row.month) }}</vs-td>
                <vs-td class="text-right font-bold text-success">{{ row.revenue_eur ? formatEur(row.revenue_eur) : '—' }}</vs-td>
                <vs-td class="text-right font-bold text-danger">
                  <a v-if="row.isTotal" class="col-link" @click="openRecap">{{ formatEur(row.expense_eur) }}</a>
                  <span v-else>{{ formatEur(row.expense_eur) }}</span>
                </vs-td>
                <vs-td class="text-right font-bold" :class="variationClass(resultExploitation(row))">
                  {{ row.revenue_eur ? formatVariation(resultExploitation(row)) : '—' }}
                </vs-td>
                <vs-td class="text-right font-medium" :class="variationClass(row.stock_variation_eur)">
                  {{ formatVariation(row.stock_variation_eur) }}
                </vs-td>
                <vs-td class="text-right font-bold" :class="variationClass(row.cashflow_eur)">
                  {{ formatVariation(row.cashflow_eur) }}
                </vs-td>
                <vs-td class="text-right">{{ row.volume_sold_m3 ? formatM3(row.volume_sold_m3) : '—' }}</vs-td>
                <vs-td class="text-right">{{ row.volume_sawn_m3 ? formatM3(row.volume_sawn_m3) : '—' }}</vs-td>
                <vs-td class="text-right">
                  <a v-if="row.capex_eur" class="col-link text-primary" @click="openInvestments">{{ formatEur(row.capex_eur) }}</a>
                  <span v-else class="text-muted">—</span>
                </vs-td>
                <vs-td class="text-right">
                  <a v-if="row.principal_eur" class="col-link col-principal" @click="openCredit('principal')">{{ formatEur(row.principal_eur) }}</a>
                  <span v-else class="text-muted">—</span>
                </vs-td>
                <vs-td class="text-right">
                  <a v-if="row.dobanda_eur" class="col-link col-dobanda" @click="openCredit('dobanda')">{{ formatEur(row.dobanda_eur) }}</a>
                  <span v-else class="text-muted">—</span>
                </vs-td>
                <vs-td class="text-right">{{ row.isTotal ? '' : (row.count || '—') }}</vs-td>
                <vs-td class="text-right">
                  <vs-button
                    v-if="!row.isTotal"
                    size="small"
                    type="border"
                    icon-pack="feather"
                    icon="icon-eye"
                    :disabled="!(row.count || row.capex_eur || row.principal_eur || row.dobanda_eur)"
                    @click="openDetail(row.month)"
                  >
                    View
                  </vs-button>
                </vs-td>
              </vs-tr>
            </template>
          </vs-table>

          <!-- ============ KPI INDUSTRIEL — XYLO IMPACT (annuel) ============ -->
          <div v-if="!loading && months.length" class="kpi-panel kpi-panel-industrial mt-8">
            <div class="kpi-panel-title">KPI industriel {{ selectedYear }} — XYLO IMPACT</div>
            <div class="kpi-grid">
              <div v-for="k in industrialKpis" :key="k.label" class="kpi-cell">
                <div class="kpi-label">{{ k.label }}</div>
                <div class="kpi-value" :class="k.cls">{{ k.value }}</div>
              </div>
            </div>
          </div>

          <!-- ============ (i) INFORMATIONS — règles de calcul du rapport ============ -->
          <div v-if="!loading" class="info-panel mt-8">
            <div class="info-head" @click="infoOpen = !infoOpen">
              <feather-icon icon="InfoIcon" svgClasses="w-4 h-4" class="mr-2" />
              <span class="info-title">Informations — d'où viennent ces chiffres</span>
              <feather-icon :icon="infoOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'" svgClasses="w-4 h-4" class="ml-auto" />
            </div>

            <div v-show="infoOpen" class="info-body">
              <!-- 1. Les deux sources -->
              <div class="info-callout">
                Ce rapport croise <b>deux sources qui n'ont rien à voir</b> :
                <ul>
                  <li>
                    <b>Les dépenses viennent des extraits bancaires</b>, pas des factures fournisseurs.
                    Une facture reçue mais non payée n'apparaît nulle part ; un acompte apparaît le jour où
                    la banque le débite. Concerne : Expenses, CAPEX, Credit (principal), Credit (dobanda), Ops.
                  </li>
                  <li>
                    <b>Le chiffre d'affaire vient de l'ERP</b> (opérations de vente / factures émises), pas
                    de la banque. Une vente facturée mais non encaissée est comptée. Concerne : Chiffre
                    d'affaire, m³ vendus, m³ sciés, Variation Stock.
                  </li>
                </ul>
                Conséquence directe : <b>les encaissements bancaires ne sont jamais affichés</b>. Seuls les
                montants négatifs (débits) sont lus. Un virement reçu — « Tr.Cred-inc.ext », « Incasare »,
                un remboursement client… — n'entre dans aucune colonne de ce tableau.
              </div>

              <!-- 2. Expenses -->
              <div class="info-sec">
                <div class="info-sec-title">Expenses EUR — comment la valeur est construite</div>
                <ol>
                  <li>
                    On lit <code>gcl_transactions</code>, <b>tous comptes bancaires confondus</b> (comptes
                    synchronisés GoCardless + comptes repris à la main depuis les extraits, ex. BRD, iBanFirst).
                  </li>
                  <li>On ne garde que les <b>débits</b> : montant &lt; 0. Les crédits sont ignorés.</li>
                  <li>
                    Le mois est celui de la <b>date de valeur</b> de l'opération (et non de la date
                    d'enregistrement en banque, qui peut tomber sur le mois suivant).
                  </li>
                  <li>
                    Conversion en EUR au <b>taux de référence BCE du jour ouvré précédant la date de valeur</b>
                    (convention BNR ; si la veille est un week-end ou un férié, on remonte au jour publié
                    précédent). Une devise inconnue de la BCE est reprise 1:1.
                  </li>
                  <li>On ajoute les <b>Manual transactions</b> saisies pour le mois (cash, compte non synchronisé).</li>
                  <li>Le montant affiché est la somme des valeurs absolues, arrondie au centime.</li>
                </ol>
              </div>

              <!-- 3. Exclusions -->
              <div class="info-sec">
                <div class="info-sec-title">Ce qui est retiré des dépenses</div>
                <p class="info-p">
                  Détecté <b>sur le libellé et la contrepartie</b> de la transaction, dans cet ordre :
                </p>
                <ul>
                  <li>
                    <b>Transferts internes XYLO → XYLO</b> (l'argent change de compte, ce n'est pas une dépense) :
                    libellé contenant « transfer intern », « transfert intern », « vers xylo impact » ; ou
                    bénéficiaire / libellé mentionnant XYLO ou IMPACT — sauf si le libellé contient
                    « comision », auquel cas la ligne est gardée comme frais bancaire ; ou libellé contenant
                    l'IBAN d'un de nos propres comptes.
                  </li>
                  <li>
                    <b>Conversions de devises</b> : « achat…vente…devises », « schimb valutar », « fxforward »,
                    « fx forward ».
                  </li>
                  <li><b>Exclusions unitaires</b> : lignes sorties à la main depuis le détail du mois.</li>
                  <li>
                    <b>Règles « éjecter opération similaire »</b> : filtre permanent sur le bénéficiaire, le
                    donneur d'ordre ou le libellé (mode exact ou « contient »). Gérées via le menu Actions.
                  </li>
                </ul>
                <p class="info-p">
                  Tout ce qui est retiré reste consultable : le total Expenses ouvre le récapitulatif, et le détail
                  du mois affiche les lignes éjectées (marquées) pour pouvoir les ré-inclure.
                </p>
              </div>

              <!-- 4. Reclassements -->
              <div class="info-sec">
                <div class="info-sec-title">Ce qui sort des dépenses courantes vers sa propre colonne</div>
                <p class="info-p">Précédence : CAPEX &gt; principal &gt; dobanda &gt; dépense courante.</p>
                <ul>
                  <li><b>CAPEX</b> — marquage manuel « → CAPEX » sur une transaction. Aucune détection automatique.</li>
                  <li><b>Credit (principal)</b> — libellé contenant « rambursare principal credit » (crédits d'investissement 022CIV et moyen/long terme 022CML).</li>
                  <li><b>Credit (dobanda)</b> — libellé contenant « dobanda » ou « comision acordare credit ».</li>
                  <li>Les tirages de <b>crédit revolving</b> (« linie de credit », 022CLC) sont éjectés par règle : ils ne sont dans aucune de ces colonnes.</li>
                </ul>
              </div>

              <!-- 5. CA et volumes -->
              <div class="info-sec">
                <div class="info-sec-title">Chiffre d'affaire, m³ vendus, m³ sciés</div>
                <ul>
                  <li>
                    <b>Chiffre d'affaire</b> — opérations de <b>vente</b> (catégorie 2) actives de l'année, montant
                    <b>HT</b>. Facture « RO… » : quantité × prix unitaire RON, converti en EUR au taux BCE ;
                    facture « EU… » : quantité × prix unitaire EUR. Le mois est celui de la <b>date de la facture</b>,
                    pas de son encaissement.
                  </li>
                  <li><b>m³ vendus</b> — Σ |quantité| × volume du produit, sur ces mêmes opérations de vente.</li>
                  <li><b>m³ sciés</b> — Σ |quantité| des produits de catégorie 38 dans les opérations de production (catégorie 3), même définition que le dashboard récolte.</li>
                </ul>
              </div>

              <!-- 6. Stock -->
              <div class="info-sec">
                <div class="info-sec-title">Variation Stock (EUR)</div>
                <ul>
                  <li>Valorisation du stock à la <b>fin du mois</b> moins celle de la <b>fin du mois précédent</b>. La base de janvier est le stock au 31 décembre de l'année précédente.</li>
                  <li>Même méthode que le rapport « History by Product Categories (m³) » : volumes des opérations actives de catégories 1 à 4 antérieures à la date de coupe, catégorie produit 29 valorisée par produit et les autres par catégorie, catégories 1 et 2 exclues.</li>
                  <li><b>Valorisation au prix courant</b> (table <code>valuations</code>) appliqué à des volumes passés : ce n'est pas une valorisation historique. Un changement de prix de valorisation modifie rétroactivement tous les mois.</li>
                  <li>Résultat mis en cache 15 minutes.</li>
                </ul>
              </div>

              <!-- 7. Colonnes calculées -->
              <div class="info-sec">
                <div class="info-sec-title">Colonnes et KPI calculés</div>
                <ul class="info-formulas">
                  <li><b>Résultat exploitation</b> = Chiffre d'affaire − Expenses</li>
                  <li><b>EBITDA Ajusté</b> = Chiffre d'affaire − Expenses + Variation Stock</li>
                  <li><b>Marge EBITDA</b> = EBITDA Ajusté ÷ Chiffre d'affaire</li>
                  <li><b>Free Cash Flow</b> = EBITDA Ajusté − CAPEX</li>
                  <li><b>Cash Flow Net</b> = Free Cash Flow − Crédit remboursé − Intérêts</li>
                  <li><b>Ops</b> = nombre de dépenses courantes du mois (hors CAPEX et crédit), transactions manuelles incluses</li>
                </ul>
              </div>

              <!-- 8. Limites -->
              <div class="info-sec">
                <div class="info-sec-title">À quoi s'attendre — limites connues</div>
                <ul>
                  <li><b>Trésorerie, pas comptabilité d'engagement.</b> Le mois d'une dépense est celui du débit bancaire, celui d'une vente est celui de la facture : les deux côtés ne sont pas alignés sur le même événement.</li>
                  <li><b>TVA.</b> Les montants bancaires sont TTC, le chiffre d'affaire est HT. Le résultat affiché est donc structurellement pessimiste.</li>
                  <li><b>Un compte bancaire non connecté = dépenses invisibles.</b> À compenser par les Manual transactions.</li>
                  <li><b>Les filtres reposent sur le texte du libellé.</b> Un libellé inhabituel peut échapper au filtre des transferts internes (et gonfler les dépenses) ou au contraire faire disparaître une vraie dépense adressée à une société contenant « XYLO » ou « IMPACT ». En cas de doute, ouvrir le détail du mois.</li>
                  <li><b>Salaires et impôts</b> ne sont pas isolés : ils sont dans Expenses comme le reste, sauf règle d'éjection explicite.</li>
                </ul>
              </div>
            </div>
          </div>
        </vx-card>
      </div>
    </div>

    <!-- ============ DETAIL POPUP ============ -->
    <vs-popup
      :title="`Expenses — ${monthLabel(detail.month)} ${selectedYear}`"
      :active.sync="detail.active"
      class="detail-popup"
    >
      <div v-if="detail.loading" class="text-center py-8 text-muted">Loading…</div>
      <div v-else>
        <div class="flex justify-between items-center mb-4">
          <div class="flex items-center">
            <span class="text-muted mr-3">{{ detail.count }} expense(s) counted</span>
            <vs-button
              size="small" type="border"
              icon-pack="feather"
              :icon="detail.sortByAmount ? 'icon-bar-chart-2' : 'icon-clock'"
              @click="detail.sortByAmount = !detail.sortByAmount"
            >{{ detail.sortByAmount ? 'Sorted by amount ↓' : 'Sort by amount' }}</vs-button>
            <vs-button
              size="small" type="border" color="success" class="ml-2"
              icon-pack="feather" icon="icon-download"
              :disabled="detail.items.length === 0"
              @click="exportXlsx"
            >Export XLSX</vs-button>
          </div>
          <span class="font-bold text-danger text-xl">{{ formatEur(detail.total_eur) }}</span>
        </div>

        <div v-if="detail.items.length === 0" class="text-center py-6 text-muted">No expenses for this month.</div>

        <div v-for="item in sortedItems" :key="item.gcl_id"
             class="detail-item mb-3 p-4 rounded-lg border"
             :class="itemRowClass(item)">
          <div class="flex justify-between items-start">
            <div class="flex-1 pr-4">
              <div class="flex items-center flex-wrap mb-1">
                <span class="font-bold mr-3">{{ formatDate(item.date) }}</span>
                <span v-if="item.account" class="badge badge-account mr-2">🏦 {{ item.account }}</span>
                <span v-if="item.manual" class="badge badge-manual">MANUAL</span>
                <span v-else-if="item.excluded_rule" class="badge badge-rule">EJECTED (rule)</span>
                <span v-else-if="item.excluded_single" class="badge badge-out">EJECTED</span>
                <span v-else-if="item.is_investment" class="badge badge-capex">CAPEX</span>
                <span v-else-if="item.category === 'principal'" class="badge badge-principal">CREDIT · principal</span>
                <span v-else-if="item.category === 'dobanda'" class="badge badge-dobanda">CREDIT · dobanda</span>
                <span v-else class="badge badge-in">counted</span>
              </div>
              <p class="text-sm font-medium mb-1">{{ item.description }}</p>
              <p v-if="item.bank_comment" class="text-xs text-muted mb-2">{{ item.bank_comment }}</p>

              <!-- per-line comment -->
              <div v-if="!item.manual" class="flex items-center mt-2">
                <vs-input
                  v-model="item.user_comment"
                  size="small"
                  placeholder="Add a comment…"
                  class="comment-input"
                  @keyup.enter="saveComment(item)"
                />
                <vs-button
                  size="small"
                  type="flat"
                  icon-pack="feather"
                  icon="icon-save"
                  class="ml-1"
                  @click="saveComment(item)"
                >Save</vs-button>
              </div>
            </div>

            <div class="text-right" style="min-width: 170px;">
              <div class="text-lg font-bold text-danger">{{ formatEur(item.amount_eur) }}</div>
              <div v-if="item.currency && item.currency !== 'EUR'" class="text-xs text-muted">
                {{ formatNative(item.amount_native, item.currency) }}<span v-if="item.rate"> · rate {{ item.rate }}</span>
              </div>

              <div class="flex flex-col items-end mt-2 gap-1">
                <template v-if="item.excluded_rule">
                  <span class="text-xs text-muted">covered by a rule</span>
                  <vs-button size="small" type="flat" color="primary" @click="openRules">Manage rules</vs-button>
                </template>
                <template v-else-if="item.is_investment">
                  <span class="text-xs text-muted">moved to Investment</span>
                  <vs-button size="small" color="success" type="border" icon-pack="feather" icon="icon-rotate-ccw"
                             @click="toggleInvestment(item, false)">Back to expenses</vs-button>
                </template>
                <template v-else-if="item.manual">
                  <span class="text-xs text-muted">manual transaction</span>
                  <vs-button size="small" color="danger" type="border" icon-pack="feather" icon="icon-trash-2"
                             @click="removeManualFromDetail(item)">Delete</vs-button>
                </template>
                <template v-else>
                  <span v-if="item.category === 'principal'" class="text-xs col-principal">in Credit (principal)</span>
                  <span v-else-if="item.category === 'dobanda'" class="text-xs col-dobanda">in Credit (dobanda)</span>
                  <vs-button
                    v-if="!item.excluded_single"
                    size="small" color="warning" type="border"
                    icon-pack="feather" icon="icon-minus-circle"
                    @click="toggleSingle(item, true)"
                  >Eject</vs-button>
                  <vs-button
                    v-else
                    size="small" color="success" type="border"
                    icon-pack="feather" icon="icon-rotate-ccw"
                    @click="toggleSingle(item, false)"
                  >Restore</vs-button>

                  <vs-button
                    v-if="!item.excluded_single"
                    size="small" color="primary" type="gradient"
                    icon-pack="feather" icon="icon-trending-up"
                    @click="toggleInvestment(item, true)"
                  >→ CAPEX</vs-button>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </vs-popup>

    <!-- ============ EJECT SIMILAR POPUP ============ -->
    <vs-popup :title="eject.item ? 'Eject similar operations' : 'New eject rule'" :active.sync="eject.active" class="eject-popup">
      <div class="p-2">
        <div v-if="eject.item" class="bg-gray-50 p-3 rounded mb-4 text-sm">
          <p><strong>Source operation:</strong> {{ eject.item.description }}</p>
          <p><strong>Amount:</strong> {{ formatEur(eject.item.amount_eur) }} · {{ formatDate(eject.item.date) }}</p>
        </div>

        <p class="text-sm text-muted mb-3">
          Choose what makes an operation “similar”. The rule removes <strong>past</strong> matches from the
          measure and keeps ejecting <strong>future</strong> ones automatically.
        </p>

        <div class="mb-3">
          <label class="text-sm font-medium">Match on</label>
          <vs-select v-model="eject.field" class="w-full" @input="onEjectCriteriaChange">
            <vs-select-item value="creditor" text="Beneficiary (counterpart)" />
            <vs-select-item value="debtor" text="Payer (debtor)" />
            <vs-select-item value="comment" text="Description / bank label" />
          </vs-select>
        </div>

        <div class="mb-3">
          <label class="text-sm font-medium">Mode</label>
          <vs-select v-model="eject.mode" class="w-full" @input="onEjectCriteriaChange">
            <vs-select-item value="exact" text="Exact match" />
            <vs-select-item value="contains" text="Contains" />
          </vs-select>
        </div>

        <div class="mb-3">
          <label class="text-sm font-medium">Value</label>
          <vs-input v-model="eject.value" class="w-full" @input="onEjectCriteriaChange" />
        </div>

        <div class="mb-4">
          <label class="text-sm font-medium">Note (optional)</label>
          <vs-input v-model="eject.note" class="w-full" placeholder="e.g. internal transfer, loan repayment…" />
        </div>

        <div class="preview-box p-3 rounded mb-4">
          <span v-if="eject.previewing" class="text-muted">Counting matches…</span>
          <span v-else-if="eject.previewCount !== null">
            <feather-icon icon="icon-target" size="16" class="mr-1 align-middle"></feather-icon>
            This will eject <strong>{{ eject.previewCount }}</strong> past operation(s)
            and all future similar ones.
          </span>
          <span v-else class="text-muted">Adjust the criteria to preview.</span>
        </div>

        <div class="flex justify-end">
          <vs-button color="secondary" type="border" class="mr-3" @click="eject.active = false">Cancel</vs-button>
          <vs-button
            color="danger" type="filled" icon-pack="feather" icon="icon-filter"
            :disabled="!eject.value || eject.previewing"
            @click="confirmEject"
          >Eject {{ eject.previewCount !== null ? '(' + eject.previewCount + ')' : '' }}</vs-button>
        </div>
      </div>
    </vs-popup>

    <!-- ============ BANK LINK CHECK POPUP ============ -->
    <vs-popup title="Bank link health check" :active.sync="bankCheck.active" class="bankcheck-popup">
      <div v-if="bankCheck.loading" class="text-center py-8 text-muted">Analysing bank links…</div>
      <div v-else>
        <div class="flex items-center mb-4 p-3 rounded-lg" :class="bankCheck.data.ok ? 'bg-green-50' : 'bg-orange-50'">
          <feather-icon :icon="bankCheck.data.ok ? 'icon-check-circle' : 'icon-alert-triangle'"
                        :class="bankCheck.data.ok ? 'text-success' : 'text-warning'" class="mr-3"></feather-icon>
          <div>
            <strong v-if="bankCheck.data.ok">All bank links look healthy.</strong>
            <strong v-else>{{ highIssues }} critical / {{ (bankCheck.data.issues || []).length }} potential issue(s) found.</strong>
            <div class="text-xs text-muted">Reference (latest synced day): {{ bankCheck.data.reference_date }}</div>
          </div>
        </div>

        <!-- Issues -->
        <div v-for="(iss, idx) in bankCheck.data.issues" :key="'iss'+idx"
             class="flex items-start p-3 mb-2 rounded border" :class="sevClass(iss.severity)">
          <span class="sev-dot mr-3" :class="'sev-'+iss.severity"></span>
          <div class="flex-1">
            <div class="flex justify-between">
              <span class="font-bold">{{ iss.account }}</span>
              <span class="badge" :class="'badge-'+iss.severity">{{ iss.title }}</span>
            </div>
            <p class="text-sm text-muted mt-1">{{ iss.detail }}</p>
          </div>
        </div>
        <div v-if="(bankCheck.data.issues || []).length === 0" class="text-center py-4 text-muted">No issues detected.</div>

        <!-- All accounts overview -->
        <h6 class="mt-5 mb-2">All accounts — last sync</h6>
        <vs-table :data="bankCheck.data.accounts || []" stripe>
          <template slot="thead">
            <vs-th>Account</vs-th>
            <vs-th>Last transaction</vs-th>
            <vs-th class="text-right">Days idle</vs-th>
            <vs-th class="text-right">Tx total</vs-th>
            <vs-th class="text-right">Tx 30d</vs-th>
          </template>
          <template slot-scope="{ data }">
            <vs-tr v-for="(a, i) in data" :key="'acc'+i">
              <vs-td>{{ a.account }}</vs-td>
              <vs-td>{{ a.last_date || '—' }}</vs-td>
              <vs-td class="text-right" :class="staleClass(a.days_stale)">{{ a.days_stale == null ? '—' : a.days_stale }}</vs-td>
              <vs-td class="text-right">{{ a.count }}</vs-td>
              <vs-td class="text-right">{{ a.count_30d }}</vs-td>
            </vs-tr>
          </template>
        </vs-table>
      </div>
    </vs-popup>

    <!-- ============ INVESTMENT (CAPEX) POPUP ============ -->
    <vs-popup :title="`Investment / CAPEX — ${selectedYear}`" :active.sync="investments.active" class="matches-popup">
      <div v-if="investments.loading" class="text-center py-8 text-muted">Loading…</div>
      <div v-else>
        <div class="flex justify-between items-center mb-3">
          <span class="text-muted">{{ investments.count }} item(s) flagged as investment</span>
          <span class="font-bold text-primary text-xl">{{ formatEur(investments.total_eur) }}</span>
        </div>
        <p class="text-sm text-muted mb-3">
          These transactions were moved out of monthly expenses and grouped here as capital expenditure.
        </p>
        <div v-if="investments.items.length === 0" class="text-center py-6 text-muted">
          No investment items yet. Use the “→ CAPEX” button on a transaction in a month's detail.
        </div>
        <div v-for="item in investments.items" :key="item.gcl_id"
             class="flex justify-between items-start p-3 mb-2 rounded border item-capex">
          <div class="flex-1 pr-4">
            <div class="flex items-center flex-wrap mb-1">
              <span class="font-bold mr-3">{{ formatDate(item.date) }}</span>
              <span v-if="item.account" class="badge badge-account mr-2">🏦 {{ item.account }}</span>
              <span class="text-xs text-muted">{{ item.currency }}</span>
            </div>
            <p class="text-sm font-medium">{{ item.description }}</p>
            <p v-if="item.bank_comment" class="text-xs text-muted">{{ item.bank_comment }}</p>
            <p v-if="item.user_comment" class="text-xs" style="color:#3730a3">💬 {{ item.user_comment }}</p>
          </div>
          <div class="text-right" style="min-width: 170px;">
            <div class="text-base font-bold text-primary">{{ formatEur(item.amount_eur) }}</div>
            <div v-if="item.currency && item.currency !== 'EUR'" class="text-xs text-muted">
              {{ formatNative(item.amount_native, item.currency) }}
            </div>
            <vs-button class="mt-2" size="small" color="success" type="border" icon-pack="feather" icon="icon-rotate-ccw"
                       @click="restoreInvestment(item)">Back to expenses</vs-button>
          </div>
        </div>
      </div>
    </vs-popup>

    <!-- ============ CREDIT (principal / dobanda) POPUP ============ -->
    <vs-popup :title="creditTitle" :active.sync="credit.active" class="matches-popup">
      <div v-if="credit.loading" class="text-center py-8 text-muted">Loading…</div>
      <div v-else>
        <div class="flex justify-between items-center mb-3">
          <span class="text-muted">{{ credit.count }} operation(s)</span>
          <span class="font-bold text-xl" :class="credit.kind === 'principal' ? 'col-principal' : 'col-dobanda'">
            {{ formatEur(credit.total_eur) }}
          </span>
        </div>
        <p class="text-sm text-muted mb-3">
          {{ credit.kind === 'principal'
            ? 'Principal repayments of term loans (022CIV investment + 022CML medium/long term), auto-detected by label and kept out of monthly expenses.'
            : 'Loan interest (« dobanda ») and origination fees (« comision acordare credit »), auto-detected by label and kept out of monthly expenses.' }}
        </p>
        <div v-if="credit.items.length === 0" class="text-center py-6 text-muted">
          No matching credit operations for {{ selectedYear }}.
        </div>
        <div v-for="item in credit.items" :key="item.gcl_id"
             class="flex justify-between items-start p-3 mb-2 rounded border"
             :class="credit.kind === 'principal' ? 'item-principal' : 'item-dobanda'">
          <div class="flex-1 pr-4">
            <div class="flex items-center flex-wrap mb-1">
              <span class="font-bold mr-3">{{ formatDate(item.date) }}</span>
              <span v-if="item.account" class="badge badge-account mr-2">🏦 {{ item.account }}</span>
              <span class="text-xs text-muted">{{ item.currency }}</span>
            </div>
            <p class="text-sm font-medium">{{ item.description }}</p>
            <p v-if="item.bank_comment" class="text-xs text-muted">{{ item.bank_comment }}</p>
            <p v-if="item.user_comment" class="text-xs" style="color:#3730a3">💬 {{ item.user_comment }}</p>
          </div>
          <div class="text-right" style="min-width: 170px;">
            <div class="text-base font-bold" :class="credit.kind === 'principal' ? 'col-principal' : 'col-dobanda'">
              {{ formatEur(item.amount_eur) }}
            </div>
            <div v-if="item.currency && item.currency !== 'EUR'" class="text-xs text-muted">
              {{ formatNative(item.amount_native, item.currency) }}
            </div>
          </div>
        </div>
      </div>
    </vs-popup>

    <!-- ============ EXPENSES RECAP POPUP ============ -->
    <vs-popup :title="`Expenses calculation — ${selectedYear}`" :active.sync="recap.active" class="recap-popup">
      <div v-if="recap.loading" class="text-center py-8 text-muted">Loading…</div>
      <div v-else class="recap-body">
        <!-- Total OUT (gross) -->
        <div class="recap-row recap-head">
          <span>Total OUT <span class="text-xs text-muted">(all outflows · {{ recap.total_out_count }} ops)</span></span>
          <span class="font-bold">{{ formatEur(recap.total_out) }}</span>
        </div>

        <!-- Ejected / excluded -->
        <div class="recap-section">Ejected / excluded</div>
        <div class="recap-row" v-for="r in recapEjectedRows" :key="r.key">
          <span><span class="recap-dot" :style="{ background: r.color }"></span>{{ r.label }}
            <span class="text-xs text-muted">({{ r.count }})</span></span>
          <span class="text-danger">−{{ formatEur(r.eur) }}</span>
        </div>
        <div class="recap-row recap-subtotal">
          <span>Total ejected</span>
          <span class="text-danger font-bold">−{{ formatEur(recapEjectedSubtotal) }}</span>
        </div>

        <!-- Reclassified into other columns -->
        <div class="recap-section">Reclassified (other columns)</div>
        <div class="recap-row" v-for="r in recapReclassifiedRows" :key="r.key">
          <span><span class="recap-dot" :style="{ background: r.color }"></span>{{ r.label }}
            <span class="text-xs text-muted">({{ r.count }})</span></span>
          <span class="text-danger">−{{ formatEur(r.eur) }}</span>
        </div>
        <div class="recap-row recap-subtotal">
          <span>Total reclassified</span>
          <span class="text-danger font-bold">−{{ formatEur(recapReclassifiedSubtotal) }}</span>
        </div>

        <!-- Net -->
        <div class="recap-row recap-total">
          <span class="font-bold">= Expenses EUR (net) <span class="text-xs text-muted">({{ recapBucket('expense').count }} ops)</span></span>
          <span class="text-2xl font-bold text-danger">{{ formatEur(recap.total_expense) }}</span>
        </div>
        <p class="text-xs text-muted mt-3">
          Internal XYLO → XYLO transfers, FX conversions, eject rules and manual exclusions are removed;
          CAPEX and credit (principal / dobanda) are moved to their own columns. The rest is the net expenses figure.
        </p>
      </div>
    </vs-popup>

    <!-- ============ MANUAL TRANSACTIONS POPUP ============ -->
    <vs-popup :title="`Manual transactions — ${selectedYear}`" :active.sync="manualTx.active" class="rules-popup">
      <div class="p-1">
        <p class="text-sm text-muted mb-3">
          Add a cash payment or a transaction from a non-synced account. It is counted as an
          <strong>expense</strong> for its month (converted to EUR at the day's ECB rate).
        </p>
        <!-- Form -->
        <div class="manual-form p-3 rounded mb-4">
          <div class="vx-row">
            <div class="vx-col w-1/3 mb-2">
              <label class="text-xs text-muted">Date</label>
              <vs-input type="date" v-model="manualTx.form.date" class="w-full" />
            </div>
            <div class="vx-col w-1/3 mb-2">
              <label class="text-xs text-muted">Amount</label>
              <vs-input type="number" min="0" step="0.01" v-model="manualTx.form.amount" class="w-full" />
            </div>
            <div class="vx-col w-1/3 mb-2">
              <label class="text-xs text-muted">Currency</label>
              <vs-select v-model="manualTx.form.currency" class="w-full">
                <vs-select-item value="EUR" text="EUR" />
                <vs-select-item value="RON" text="RON" />
              </vs-select>
            </div>
            <div class="vx-col w-2/3 mb-2">
              <label class="text-xs text-muted">Label</label>
              <vs-input v-model="manualTx.form.label" placeholder="e.g. cash payment supplier X" class="w-full" />
            </div>
            <div class="vx-col w-1/3 mb-2">
              <label class="text-xs text-muted">Account</label>
              <vs-input v-model="manualTx.form.account" placeholder="Cash / BRD…" class="w-full" />
            </div>
          </div>
          <div class="flex justify-end mt-2">
            <vs-button size="small" color="primary"
                       :disabled="manualTx.saving || !manualTx.form.date || !(parseFloat(manualTx.form.amount) > 0)"
                       @click="saveManualTx">{{ manualTx.saving ? 'Saving…' : 'Add expense' }}</vs-button>
          </div>
        </div>

        <!-- List -->
        <div v-if="manualTx.loading" class="text-center py-6 text-muted">Loading…</div>
        <div v-else>
          <div class="flex justify-between items-center mb-2">
            <span class="text-muted">{{ manualTx.count }} manual transaction(s)</span>
            <span class="font-bold text-danger">{{ formatEur(manualTx.total_eur) }}</span>
          </div>
          <div v-if="manualTx.items.length === 0" class="text-center py-4 text-muted">No manual transactions yet.</div>
          <div v-for="item in manualTx.items" :key="item.id"
               class="flex justify-between items-center p-2 mb-2 rounded border item-ejected">
            <div class="flex-1 pr-3">
              <span class="font-bold mr-3">{{ formatDate(item.date) }}</span>
              <span v-if="item.account" class="badge badge-account mr-2">🏦 {{ item.account }}</span>
              <span class="text-sm">{{ item.label || 'Manual transaction' }}</span>
            </div>
            <div class="text-right" style="min-width: 150px;">
              <span class="font-bold text-danger mr-3">{{ formatEur(item.amount_eur) }}</span>
              <span v-if="item.currency !== 'EUR'" class="text-xs text-muted mr-3">{{ formatNative(item.amount_native, item.currency) }}</span>
              <vs-button size="small" color="danger" type="flat" icon-pack="feather" icon="icon-trash-2"
                         @click="removeManualTx(item)">Delete</vs-button>
            </div>
          </div>
        </div>
      </div>
    </vs-popup>

    <!-- ============ RULES MANAGER POPUP ============ -->
    <vs-popup title="Eject rules" :active.sync="rules.active" class="rules-popup">
      <div v-if="rules.loading" class="text-center py-6 text-muted">Loading…</div>
      <div v-else>
        <div class="flex justify-between items-center mb-3">
          <p class="text-sm text-muted mr-4 mb-0">
            Active rules remove matching operations (past &amp; future) from the measure.
            Delete a rule to bring those operations back.
          </p>
          <vs-button size="small" color="danger" type="filled" icon-pack="feather" icon="icon-plus"
                     class="flex-shrink-0" @click="openNewRule">Add rule</vs-button>
        </div>
        <div v-if="rules.list.length === 0" class="text-center py-6 text-muted">No rules yet.</div>
        <div v-for="r in rules.list" :key="r.id" class="flex justify-between items-center p-3 mb-2 rounded border">
          <div>
            <div class="text-sm">
              <span class="badge badge-rule mr-2">{{ fieldLabel(r.field) }}</span>
              <span class="font-medium">{{ r.mode }}</span> :
              <span class="font-bold">{{ r.value }}</span>
            </div>
            <div class="text-xs text-muted mt-1">
              ~{{ r.match_count }} op(s) at creation<span v-if="r.note"> · {{ r.note }}</span>
            </div>
          </div>
          <div class="flex gap-1">
            <vs-button size="small" color="primary" type="border" icon-pack="feather" icon="icon-eye"
                       @click="openRuleMatches(r)">View ops</vs-button>
            <vs-button size="small" color="danger" type="border" icon-pack="feather" icon="icon-trash-2"
                       @click="removeRule(r)">Delete</vs-button>
          </div>
        </div>
      </div>
    </vs-popup>

    <!-- ============ RULE MATCHES (nested) POPUP ============ -->
    <vs-popup
      :title="ruleMatches.rule ? `Ejected in ${selectedYear}: ${fieldLabel(ruleMatches.rule.field)} ${ruleMatches.rule.mode} « ${ruleMatches.rule.value} »` : 'Ejected operations'"
      :active.sync="ruleMatches.active"
      class="matches-popup"
    >
      <div v-if="ruleMatches.loading" class="text-center py-6 text-muted">Loading…</div>
      <div v-else>
        <div class="flex justify-between items-center mb-3">
          <div class="flex items-center">
            <span class="text-muted mr-3">{{ ruleMatches.count }} operation(s)</span>
            <vs-button
              size="small" type="border"
              icon-pack="feather"
              :icon="ruleMatches.sortByAmount ? 'icon-bar-chart-2' : 'icon-clock'"
              @click="ruleMatches.sortByAmount = !ruleMatches.sortByAmount"
            >{{ ruleMatches.sortByAmount ? 'Sorted by amount ↓' : 'Sort by amount' }}</vs-button>
          </div>
          <span class="font-bold text-danger text-lg">{{ formatEur(ruleMatches.total_eur) }}</span>
        </div>
        <div v-if="ruleMatches.count > ruleMatches.items.length" class="text-xs text-muted mb-2">
          Showing first {{ ruleMatches.items.length }} of {{ ruleMatches.count }}.
        </div>
        <div v-if="ruleMatches.items.length === 0" class="text-center py-6 text-muted">No matching operations.</div>
        <div v-for="item in sortedMatches" :key="item.gcl_id"
             class="flex justify-between items-start p-3 mb-2 rounded border item-ejected">
          <div class="flex-1 pr-4">
            <div class="flex items-center flex-wrap mb-1">
              <span class="font-bold mr-3">{{ formatDate(item.date) }}</span>
              <span v-if="item.account" class="badge badge-account mr-2">🏦 {{ item.account }}</span>
              <span class="text-xs text-muted">{{ item.currency }}</span>
            </div>
            <p class="text-sm font-medium">{{ item.description }}</p>
            <p v-if="item.bank_comment" class="text-xs text-muted">{{ item.bank_comment }}</p>
          </div>
          <div class="text-right" style="min-width: 150px;">
            <div class="text-base font-bold text-danger">{{ formatEur(item.amount_eur) }}</div>
            <div v-if="item.currency && item.currency !== 'EUR'" class="text-xs text-muted">
              {{ formatNative(item.amount_native, item.currency) }}
            </div>
          </div>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import XLSX from 'xlsx'

export default {
  name: 'AccountingView',
  data() {
    return {
      loading: false,
      exporting: false,
      infoOpen: false,
      selectedYear: 2026,
      months: [],
      totalYear: 0,
      totalCapex: 0,
      totalPrincipal: 0,
      totalDobanda: 0,
      totalRevenue: 0,
      totalCashflow: 0,
      totalRevenueRonNative: 0,
      totalRevenueEurNative: 0,
      totalVolumeSold: 0,
      totalVolumeSawn: 0,
      stockValue: null,
      stockVariationYtd: null,
      monthNames: [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ],
      detail: { active: false, loading: false, month: 1, items: [], total_eur: 0, count: 0, sortByAmount: false },
      eject: { active: false, item: null, field: 'creditor', mode: 'exact', value: '', note: '', previewCount: null, previewing: false },
      rules: { active: false, loading: false, list: [] },
      ruleMatches: { active: false, loading: false, rule: null, items: [], count: 0, total_eur: 0, sortByAmount: true },
      bankCheck: { active: false, loading: false, data: {} },
      investments: { active: false, loading: false, items: [], count: 0, total_eur: 0 },
      credit: { active: false, loading: false, kind: 'principal', items: [], count: 0, total_eur: 0 },
      recap: { active: false, loading: false, total_out: 0, total_out_count: 0, total_removed: 0, total_expense: 0, buckets: {} },
      manualTx: {
        active: false, loading: false, saving: false, items: [], count: 0, total_eur: 0,
        form: { date: '', amount: '', currency: 'EUR', label: '', account: '' }
      }
    }
  },
  computed: {
    availableYears() {
      const current = new Date().getFullYear()
      const years = []
      for (let y = Math.max(current, 2026); y >= 2026; y--) years.push(y)
      return years
    },
    sortedItems() {
      if (!this.detail.sortByAmount) return this.detail.items
      return this.detail.items.slice().sort((a, b) => b.amount_eur - a.amount_eur)
    },
    sortedMatches() {
      if (!this.ruleMatches.sortByAmount) return this.ruleMatches.items
      return this.ruleMatches.items.slice().sort((a, b) => b.amount_eur - a.amount_eur)
    },
    highIssues() {
      return (this.bankCheck.data.issues || []).filter((i) => i.severity === 'high').length
    },
    // Lignes du tableau = mois + une ligne TOTAL synthétique (vraie ligne de données → alignée par vuesax).
    tableRows() {
      if (!this.months || !this.months.length) return this.months
      const total = {
        month: 0,
        isTotal: true,
        revenue_eur: this.totalRevenue,
        expense_eur: this.totalYear,
        capex_eur: this.totalCapex,
        principal_eur: this.totalPrincipal,
        dobanda_eur: this.totalDobanda,
        stock_variation_eur: this.stockVariationYtd,
        cashflow_eur: this.totalCashflow,
        volume_sold_m3: this.totalVolumeSold,
        volume_sawn_m3: this.totalVolumeSawn,
        count: null
      }
      return [...this.months, total]
    },
    // ----- KPI synthétiques (dérivés des totaux, aucun appel API) -----
    kpiFigures() {
      const ca = this.totalRevenue || 0
      const expenses = this.totalYear || 0
      const stockVar = this.stockVariationYtd || 0
      const ebitdaOp = ca - expenses
      const ebitdaAdj = ebitdaOp + stockVar
      const margin = ca ? (ebitdaAdj / ca) * 100 : null
      const fcf = ebitdaAdj - (this.totalCapex || 0)
      const cashNet = fcf - (this.totalPrincipal || 0) - (this.totalDobanda || 0)
      return { ca, expenses, ebitdaOp, ebitdaAdj, margin, fcf, cashNet }
    },
    financialKpis() {
      const k = this.kpiFigures
      return [
        { label: "Chiffre d'affaires", value: this.formatEur(k.ca) },
        { label: 'Expenses', value: this.formatEur(k.expenses) },
        { label: 'EBITDA Opérationnel', value: this.formatVariation(k.ebitdaOp), cls: this.variationClass(k.ebitdaOp) },
        { label: 'Variation Stock', value: this.formatVariation(this.stockVariationYtd), cls: this.variationClass(this.stockVariationYtd) },
        { label: 'EBITDA Ajusté', value: this.formatVariation(k.ebitdaAdj), cls: this.variationClass(k.ebitdaAdj) },
        { label: 'Marge EBITDA', value: this.formatPct(k.margin) },
        { label: 'CAPEX', value: this.formatEur(this.totalCapex) },
        { label: 'Free Cash Flow', value: this.formatVariation(k.fcf), cls: this.variationClass(k.fcf) },
        { label: 'Crédit remboursé', value: this.formatEur(this.totalPrincipal) },
        { label: 'Intérêts', value: this.formatEur(this.totalDobanda) },
        { label: 'Cash Flow Net', value: this.formatVariation(k.cashNet), cls: this.variationClass(k.cashNet), strong: true }
      ]
    },
    industrialKpis() {
      const ca = this.totalRevenue || 0
      const sold = this.totalVolumeSold || 0
      const sawn = this.totalVolumeSawn || 0
      const ebitdaAdj = this.kpiFigures.ebitdaAdj
      return [
        { label: "Chiffre d'affaires", value: this.formatEur(ca) },
        { label: 'm³ vendus', value: this.formatM3(sold) },
        { label: 'm³ sciés', value: this.formatM3(sawn) },
        { label: 'CA / m³ vendu', value: sold ? this.formatPerM3(ca / sold) : '—' },
        { label: 'Expenses / m³ scié', value: sawn ? this.formatPerM3(this.totalYear / sawn) : '—' },
        { label: 'EBITDA / m³ vendu', value: sold ? this.formatPerM3(ebitdaAdj / sold) : '—' },
        { label: 'Valeur du stock', value: this.stockValue === null ? '—' : this.formatEur(this.stockValue) },
        { label: 'Variation du stock', value: this.formatVariation(this.stockVariationYtd), cls: this.variationClass(this.stockVariationYtd) },
        { label: 'CAPEX cumulé', value: this.formatEur(this.totalCapex) }
      ]
    },
    creditTitle() {
      const label = this.credit.kind === 'principal' ? 'Credit · principal' : 'Credit · dobanda'
      return `${label} — ${this.selectedYear}`
    },
    recapEjectedRows() {
      return [
        { key: 'internal', label: 'Internal transfers (XYLO → XYLO)', color: '#9ca3af', ...this.recapBucket('internal') },
        { key: 'rule', label: 'Ejected by rule', color: '#f59e0b', ...this.recapBucket('rule') },
        { key: 'single', label: 'Ejected manually', color: '#ef4444', ...this.recapBucket('single') }
      ]
    },
    recapReclassifiedRows() {
      return [
        { key: 'capex', label: 'CAPEX / Investment', color: '#7367f0', ...this.recapBucket('capex') },
        { key: 'principal', label: 'Credit (principal)', color: '#2563eb', ...this.recapBucket('principal') },
        { key: 'dobanda', label: 'Credit (dobanda)', color: '#2563eb', ...this.recapBucket('dobanda') }
      ]
    },
    recapEjectedSubtotal() {
      return this.recapEjectedRows.reduce((s, r) => s + r.eur, 0)
    },
    recapReclassifiedSubtotal() {
      return this.recapReclassifiedRows.reduce((s, r) => s + r.eur, 0)
    }
  },
  mounted() {
    this.loadExpenses()
  },
  methods: {
    /* ---------- monthly table ---------- */
    loadExpenses() {
      this.loading = true
      this.$store.dispatch('BankList/fetchMonthlyExpenses', { year: this.selectedYear })
        .then((res) => {
          const data = res.data || {}
          this.months = data.months || []
          this.totalYear = data.total_eur || 0
          this.totalCapex = data.total_capex || 0
          this.totalPrincipal = data.total_principal || 0
          this.totalDobanda = data.total_dobanda || 0
          this.totalRevenue = data.total_revenue_eur || 0
          this.totalCashflow = data.total_cashflow_eur || 0
          this.totalRevenueRonNative = data.total_revenue_ron_native || 0
          this.totalRevenueEurNative = data.total_revenue_eur_native || 0
          this.totalVolumeSold = data.total_volume_sold_m3 || 0
          this.totalVolumeSawn = data.total_volume_sawn_m3 || 0
          this.stockValue = data.stock_value_eur === undefined ? null : data.stock_value_eur
          this.stockVariationYtd = data.stock_variation_ytd === undefined ? null : data.stock_variation_ytd
        })
        .catch((err) => {
          console.error('Error loading monthly expenses:', err)
          this.$vs.notify({ color: 'danger', title: 'Error', text: 'Could not load monthly expenses.' })
          this.months = []
          this.totalYear = 0
          this.totalCapex = 0
          this.totalPrincipal = 0
          this.totalDobanda = 0
          this.totalRevenue = 0
          this.totalCashflow = 0
          this.totalRevenueRonNative = 0
          this.totalRevenueEurNative = 0
          this.totalVolumeSold = 0
          this.totalVolumeSawn = 0
          this.stockValue = null
          this.stockVariationYtd = null
        })
        .finally(() => { this.loading = false })
    },

    /* ---------- bank link health ---------- */
    openBankCheck() {
      this.bankCheck.active = true
      this.bankCheck.loading = true
      this.bankCheck.data = {}
      this.$store.dispatch('BankList/fetchBankSyncHealth')
        .then((res) => { this.bankCheck.data = res.data || {} })
        .catch(() => { this.$vs.notify({ color: 'danger', title: 'Error', text: 'Could not run bank check.' }) })
        .finally(() => { this.bankCheck.loading = false })
    },
    sevClass(sev) {
      return sev === 'high' ? 'iss-high' : (sev === 'medium' ? 'iss-medium' : 'iss-low')
    },
    staleClass(days) {
      if (days == null) return 'text-muted'
      if (days >= 30) return 'text-danger font-bold'
      if (days >= 10) return 'text-warning font-bold'
      return ''
    },

    /* ---------- full-year export ---------- */
    // Feuille « Règles de calcul » de l'export — reprend le panneau (i) de la page pour que
    // le XLSX soit compréhensible sans l'écran. À garder synchronisé avec ce panneau.
    exportRulesRows() {
      const t = (title) => [[''], [title]]
      return [
        [`Rapport comptabilité ${this.selectedYear} — d'où viennent les chiffres`],
        [''],
        ['Ce rapport croise deux sources qui n\'ont rien à voir :'],
        ["  · Les dépenses viennent des EXTRAITS BANCAIRES, pas des factures fournisseurs. Une facture non payée n'apparaît nulle part ; un acompte apparaît le jour du débit."],
        ["    Concerne : Expenses, CAPEX, Credit (principal), Credit (dobanda), Ops."],
        ["  · Le chiffre d'affaire vient de l'ERP (factures de vente émises), pas de la banque. Une vente facturée non encaissée est comptée."],
        ["    Concerne : Chiffre d'affaire, m³ vendus, m³ sciés, Variation Stock."],
        ["  · Conséquence : les ENCAISSEMENTS bancaires ne sont jamais affichés. Seuls les débits sont lus."],
        ...t('EXPENSES EUR — construction de la valeur'),
        ["1. Toutes les transactions bancaires, tous comptes confondus (GoCardless + comptes repris à la main : BRD, iBanFirst)."],
        ['2. Débits uniquement (montant < 0). Les crédits sont ignorés.'],
        ["3. Le mois est celui de la DATE DE VALEUR (pas de la date d'enregistrement en banque)."],
        ['4. Conversion en EUR au taux BCE du jour ouvré PRÉCÉDANT la date de valeur (convention BNR). Devise inconnue de la BCE : reprise 1:1.'],
        ['5. On ajoute les Manual transactions du mois (cash, compte non synchronisé).'],
        ...t('CE QUI EST RETIRÉ DES DÉPENSES (détecté sur le libellé et la contrepartie)'),
        ["  · Transferts internes XYLO -> XYLO : « transfer intern », « transfert intern », « vers xylo impact » ; bénéficiaire ou libellé mentionnant XYLO / IMPACT (sauf si le libellé contient « comision » : gardé comme frais bancaire) ; libellé contenant l'IBAN d'un de nos propres comptes."],
        ['  · Conversions de devises : « achat…vente…devises », « schimb valutar », « fxforward », « fx forward ».'],
        ['  · Exclusions unitaires posées à la main depuis le détail du mois.'],
        ['  · Règles « éjecter opération similaire » sur bénéficiaire / donneur d\'ordre / libellé (exact ou contient).'],
        ["  · Toutes ces lignes restent dans la feuille « All expenses », colonne Status."],
        ...t('CE QUI SORT VERS SA PROPRE COLONNE (précédence CAPEX > principal > dobanda)'),
        ['  · CAPEX : marquage manuel « -> CAPEX ». Aucune détection automatique.'],
        ['  · Credit (principal) : libellé contenant « rambursare principal credit » (022CIV et 022CML).'],
        ['  · Credit (dobanda) : libellé contenant « dobanda » ou « comision acordare credit ».'],
        ['  · Crédit revolving (« linie de credit », 022CLC) : éjecté par règle, dans aucune colonne.'],
        ...t("CHIFFRE D'AFFAIRE, m³ VENDUS, m³ SCIÉS"),
        ["  · Chiffre d'affaire : opérations de vente (catégorie 2) actives, montant HT. Facture RO… = quantité × prix RON converti au taux BCE ; facture EU… = quantité × prix EUR. Mois = date de facture, pas d'encaissement."],
        ['  · m³ vendus : Σ |quantité| × volume produit sur ces mêmes opérations de vente.'],
        ['  · m³ sciés : Σ |quantité| des produits de catégorie 38 dans les opérations de production (catégorie 3).'],
        ...t('VARIATION STOCK (EUR)'),
        ['  · Valorisation fin de mois moins valorisation fin du mois précédent. Base de janvier = stock au 31 décembre de l\'année précédente.'],
        ['  · Même méthode que le rapport « History by Product Categories (m³) » : opérations actives catégories 1 à 4 antérieures à la coupe, catégorie produit 29 valorisée par produit, les autres par catégorie, catégories 1 et 2 exclues.'],
        ['  · Valorisation au PRIX COURANT appliqué à des volumes passés : non historique. Un changement de prix de valorisation modifie rétroactivement tous les mois.'],
        ...t('COLONNES ET KPI CALCULÉS'),
        ["  Résultat exploitation = Chiffre d'affaire − Expenses"],
        ["  EBITDA Ajusté        = Chiffre d'affaire − Expenses + Variation Stock"],
        ["  Marge EBITDA         = EBITDA Ajusté ÷ Chiffre d'affaire"],
        ['  Free Cash Flow       = EBITDA Ajusté − CAPEX'],
        ['  Cash Flow Net        = Free Cash Flow − Crédit remboursé − Intérêts'],
        ['  Ops                  = nombre de dépenses courantes du mois (hors CAPEX et crédit), Manual transactions incluses'],
        ...t('LIMITES CONNUES'),
        ["  · Trésorerie, pas comptabilité d'engagement : le mois d'une dépense est celui du débit, celui d'une vente est celui de la facture."],
        ["  · TVA : les montants bancaires sont TTC, le chiffre d'affaire est HT. Le résultat affiché est structurellement pessimiste."],
        ['  · Un compte bancaire non connecté = dépenses invisibles (à compenser par les Manual transactions).'],
        ["  · Les filtres reposent sur le texte du libellé : un libellé inhabituel peut échapper au filtre des transferts internes, ou faire disparaître une vraie dépense adressée à une société contenant « XYLO » ou « IMPACT »."],
        ['  · Salaires et impôts ne sont pas isolés : ils sont dans Expenses, sauf règle d\'éjection explicite.']
      ]
    },
    exportYear() {
      this.exporting = true
      this.$store.dispatch('BankList/fetchExpenseExport', { year: this.selectedYear })
        .then((res) => {
          const data = res.data || {}
          const items = data.items || []

          // Sheet 1 — récap mensuel : MÊMES colonnes, même ordre et mêmes libellés que le
          // tableau à l'écran, en valeurs numériques brutes (recalculables dans Excel).
          const summaryHeader = [
            'Month', "Chiffre d'affaire", 'Expenses EUR', 'Résultat exploitation',
            'Variation Stock (EUR)', 'EBITDA Ajusté', 'm³ vendus', 'm³ sciés',
            'CAPEX', 'Credit (principal)', 'Credit (dobanda)', 'Ops'
          ]
          const nz = (v) => (v === null || v === undefined ? '' : v)
          const summary = this.months.map((m) => ({
            Month: this.monthLabel(m.month),
            "Chiffre d'affaire": m.revenue_eur,
            'Expenses EUR': m.expense_eur,
            'Résultat exploitation': this.resultExploitation(m),
            'Variation Stock (EUR)': nz(m.stock_variation_eur),
            'EBITDA Ajusté': m.cashflow_eur,
            'm³ vendus': m.volume_sold_m3,
            'm³ sciés': m.volume_sawn_m3,
            CAPEX: m.capex_eur,
            'Credit (principal)': m.principal_eur,
            'Credit (dobanda)': m.dobanda_eur,
            Ops: m.count
          }))
          summary.push({})
          summary.push({
            Month: 'TOTAL ' + this.selectedYear,
            "Chiffre d'affaire": this.totalRevenue,
            'Expenses EUR': this.totalYear,
            'Résultat exploitation': this.resultExploitation({ revenue_eur: this.totalRevenue, expense_eur: this.totalYear }),
            'Variation Stock (EUR)': nz(this.stockVariationYtd),
            'EBITDA Ajusté': this.totalCashflow,
            'm³ vendus': this.totalVolumeSold,
            'm³ sciés': this.totalVolumeSawn,
            CAPEX: this.totalCapex,
            'Credit (principal)': this.totalPrincipal,
            'Credit (dobanda)': this.totalDobanda
          })
          const wsSummary = XLSX.utils.json_to_sheet(summary, { header: summaryHeader })

          // Sheet 2 — les deux panneaux KPI de la page, en valeurs brutes + unité.
          const k = this.kpiFigures
          const perM3 = (num, den) => (den ? num / den : '')
          const kpis = [
            { Bloc: 'Financier', Indicateur: "Chiffre d'affaires", Valeur: k.ca, Unité: 'EUR' },
            { Bloc: 'Financier', Indicateur: 'Expenses', Valeur: k.expenses, Unité: 'EUR' },
            { Bloc: 'Financier', Indicateur: 'EBITDA Opérationnel', Valeur: k.ebitdaOp, Unité: 'EUR' },
            { Bloc: 'Financier', Indicateur: 'Variation Stock', Valeur: nz(this.stockVariationYtd), Unité: 'EUR' },
            { Bloc: 'Financier', Indicateur: 'EBITDA Ajusté', Valeur: k.ebitdaAdj, Unité: 'EUR' },
            { Bloc: 'Financier', Indicateur: 'Marge EBITDA', Valeur: k.margin === null ? '' : k.margin, Unité: '%' },
            { Bloc: 'Financier', Indicateur: 'CAPEX', Valeur: this.totalCapex, Unité: 'EUR' },
            { Bloc: 'Financier', Indicateur: 'Free Cash Flow', Valeur: k.fcf, Unité: 'EUR' },
            { Bloc: 'Financier', Indicateur: 'Crédit remboursé', Valeur: this.totalPrincipal, Unité: 'EUR' },
            { Bloc: 'Financier', Indicateur: 'Intérêts', Valeur: this.totalDobanda, Unité: 'EUR' },
            { Bloc: 'Financier', Indicateur: 'Cash Flow Net', Valeur: k.cashNet, Unité: 'EUR' },
            { Bloc: 'Industriel', Indicateur: 'm³ vendus', Valeur: this.totalVolumeSold, Unité: 'm³' },
            { Bloc: 'Industriel', Indicateur: 'm³ sciés', Valeur: this.totalVolumeSawn, Unité: 'm³' },
            { Bloc: 'Industriel', Indicateur: 'CA / m³ vendu', Valeur: perM3(k.ca, this.totalVolumeSold), Unité: 'EUR/m³' },
            { Bloc: 'Industriel', Indicateur: 'Expenses / m³ scié', Valeur: perM3(this.totalYear, this.totalVolumeSawn), Unité: 'EUR/m³' },
            { Bloc: 'Industriel', Indicateur: 'EBITDA / m³ vendu', Valeur: perM3(k.ebitdaAdj, this.totalVolumeSold), Unité: 'EUR/m³' },
            { Bloc: 'Industriel', Indicateur: 'Valeur du stock', Valeur: nz(this.stockValue), Unité: 'EUR' },
            { Bloc: 'Industriel', Indicateur: 'CAPEX cumulé', Valeur: this.totalCapex, Unité: 'EUR' }
          ]
          const wsKpi = XLSX.utils.json_to_sheet(kpis, { header: ['Bloc', 'Indicateur', 'Valeur', 'Unité'] })

          // Sheet 3 — every line of the year (counted + ejected), with status
          const lines = items.map((i) => ({
            Month: this.monthLabel(i.month),
            Date: this.formatDate(i.date),
            Account: i.account || '',
            Description: i.description,
            Beneficiary: i.creditor,
            Payer: i.debtor,
            Currency: i.currency,
            'Amount (native)': i.amount_native,
            'Amount EUR': i.amount_eur,
            Rate: i.rate || '',
            Status: this.statusLabel(i),
            Comment: i.user_comment || ''
          }))
          const wsLines = XLSX.utils.json_to_sheet(lines, {
            header: ['Month', 'Date', 'Account', 'Description', 'Beneficiary', 'Payer', 'Currency', 'Amount (native)', 'Amount EUR', 'Rate', 'Status', 'Comment']
          })

          // Sheet 4 — règles de calcul (même contenu que le panneau (i) de la page), pour que
          // le fichier reste lisible seul, sans l'écran.
          const wsRules = XLSX.utils.aoa_to_sheet(this.exportRulesRows())

          const wb = XLSX.utils.book_new()
          XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary')
          XLSX.utils.book_append_sheet(wb, wsKpi, 'KPI')
          XLSX.utils.book_append_sheet(wb, wsLines, 'All expenses')
          XLSX.utils.book_append_sheet(wb, wsRules, 'Règles de calcul')
          XLSX.writeFile(wb, `expenses_${this.selectedYear}.xlsx`)
          this.$vs.notify({ color: 'success', title: 'Export', text: `${items.length} expense lines exported.` })
        })
        .catch((err) => {
          console.error('Export error:', err)
          this.$vs.notify({ color: 'danger', title: 'Error', text: 'Could not export.' })
        })
        .finally(() => { this.exporting = false })
    },

    /* ---------- detail popup ---------- */
    openDetail(month) {
      this.detail.month = month
      this.detail.active = true
      this.loadDetail()
    },
    loadDetail() {
      this.detail.loading = true
      this.$store.dispatch('BankList/fetchExpenseDetail', { year: this.selectedYear, month: this.detail.month })
        .then((res) => {
          const data = res.data || {}
          this.detail.items = data.items || []
          this.detail.total_eur = data.total_eur || 0
          this.detail.count = data.count || 0
        })
        .catch((err) => {
          console.error('Error loading detail:', err)
          this.$vs.notify({ color: 'danger', title: 'Error', text: 'Could not load detail.' })
        })
        .finally(() => { this.detail.loading = false })
    },
    isEjected(item) {
      return item.excluded_single || item.excluded_rule
    },
    statusLabel(i) {
      if (i.excluded_rule) return 'Ejected (rule)'
      if (i.excluded_single) return 'Ejected'
      if (i.is_investment) return 'CAPEX'
      if (i.category === 'principal') return 'Credit · principal'
      if (i.category === 'dobanda') return 'Credit · dobanda'
      return 'Counted'
    },
    exportXlsx() {
      const rows = this.sortedItems.map((i) => ({
        Date: this.formatDate(i.date),
        Account: i.account || '',
        Description: i.description,
        Beneficiary: i.creditor,
        Payer: i.debtor,
        Currency: i.currency,
        'Amount (native)': i.amount_native,
        'Amount EUR': i.amount_eur,
        Rate: i.rate || '',
        Status: i.excluded_rule ? 'Ejected (rule)' : (i.excluded_single ? 'Ejected' : 'Counted'),
        Comment: i.user_comment || ''
      }))
      // total row (counted only)
      rows.push({})
      rows.push({ Description: 'TOTAL (counted)', 'Amount EUR': this.detail.total_eur })

      const ws = XLSX.utils.json_to_sheet(rows, {
        header: ['Date', 'Account', 'Description', 'Beneficiary', 'Payer', 'Currency', 'Amount (native)', 'Amount EUR', 'Rate', 'Status', 'Comment']
      })
      const wb = XLSX.utils.book_new()
      const sheetName = `${this.monthLabel(this.detail.month)} ${this.selectedYear}`.slice(0, 31)
      XLSX.utils.book_append_sheet(wb, ws, sheetName)
      XLSX.writeFile(wb, `expenses_${this.selectedYear}_${String(this.detail.month).padStart(2, '0')}.xlsx`)
    },
    saveComment(item) {
      this.$store.dispatch('BankList/setExpenseComment', { gcl_id: item.gcl_id, comment: item.user_comment })
        .then(() => { this.$vs.notify({ color: 'success', title: 'Saved', text: 'Comment saved.' }) })
        .catch(() => { this.$vs.notify({ color: 'danger', title: 'Error', text: 'Could not save comment.' }) })
    },
    toggleSingle(item, excluded) {
      this.$store.dispatch('BankList/setExpenseExcluded', { gcl_id: item.gcl_id, excluded })
        .then(() => {
          item.excluded_single = excluded
          this.refreshAll()
        })
        .catch(() => { this.$vs.notify({ color: 'danger', title: 'Error', text: 'Could not update.' }) })
    },
    toggleInvestment(item, isInv) {
      this.$store.dispatch('BankList/setExpenseInvestment', { gcl_id: item.gcl_id, is_investment: isInv })
        .then(() => {
          item.is_investment = isInv
          this.$vs.notify({ color: 'success', title: isInv ? 'Moved to Investment' : 'Back to expenses',
            text: isInv ? 'Transaction flagged as CAPEX.' : 'Transaction returned to expenses.' })
          this.refreshAll()
          if (this.investments.active) this.loadInvestments()
        })
        .catch(() => { this.$vs.notify({ color: 'danger', title: 'Error', text: 'Could not update.' }) })
    },
    openInvestments() {
      this.investments.active = true
      this.loadInvestments()
    },
    loadInvestments() {
      this.investments.loading = true
      this.$store.dispatch('BankList/fetchInvestments', { year: this.selectedYear })
        .then((res) => {
          const data = res.data || {}
          this.investments.items = data.items || []
          this.investments.count = data.count || 0
          this.investments.total_eur = data.total_eur || 0
        })
        .catch(() => { this.$vs.notify({ color: 'danger', title: 'Error', text: 'Could not load investments.' }) })
        .finally(() => { this.investments.loading = false })
    },
    restoreInvestment(item) {
      this.$store.dispatch('BankList/setExpenseInvestment', { gcl_id: item.gcl_id, is_investment: false })
        .then(() => {
          this.investments.items = this.investments.items.filter((i) => i.gcl_id !== item.gcl_id)
          this.investments.count = this.investments.items.length
          this.investments.total_eur = this.investments.items.reduce((s, i) => s + i.amount_eur, 0)
          this.loadExpenses()
          if (this.detail.active) this.loadDetail()
        })
        .catch(() => { this.$vs.notify({ color: 'danger', title: 'Error', text: 'Could not update.' }) })
    },

    /* ---------- expenses recap ---------- */
    recapBucket(key) {
      const b = (this.recap.buckets && this.recap.buckets[key]) || {}
      return { eur: b.eur || 0, count: b.count || 0 }
    },
    openRecap() {
      this.recap.active = true
      this.loadRecap()
    },
    loadRecap() {
      this.recap.loading = true
      this.$store.dispatch('BankList/fetchExpenseRecap', { year: this.selectedYear })
        .then((res) => {
          const data = res.data || {}
          this.recap.total_out = data.total_out || 0
          this.recap.total_out_count = data.total_out_count || 0
          this.recap.total_removed = data.total_removed || 0
          this.recap.total_expense = data.total_expense || 0
          this.recap.buckets = data.buckets || {}
        })
        .catch(() => { this.$vs.notify({ color: 'danger', title: 'Error', text: 'Could not load expenses recap.' }) })
        .finally(() => { this.recap.loading = false })
    },

    /* ---------- manual transactions ---------- */
    openManualTx() {
      this.manualTx.active = true
      if (!this.manualTx.form.date) {
        const d = new Date()
        this.manualTx.form.date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      }
      this.loadManualTx()
    },
    loadManualTx() {
      this.manualTx.loading = true
      this.$store.dispatch('BankList/fetchManualTx', { year: this.selectedYear })
        .then((res) => {
          const data = res.data || {}
          this.manualTx.items = data.items || []
          this.manualTx.count = data.count || 0
          this.manualTx.total_eur = data.total_eur || 0
        })
        .catch(() => { this.$vs.notify({ color: 'danger', title: 'Error', text: 'Could not load manual transactions.' }) })
        .finally(() => { this.manualTx.loading = false })
    },
    saveManualTx() {
      const f = this.manualTx.form
      if (!f.date || !(parseFloat(f.amount) > 0)) return
      this.manualTx.saving = true
      this.$store.dispatch('BankList/createManualTx', {
        date: f.date, amount: parseFloat(f.amount), currency: f.currency, label: f.label, account: f.account
      })
        .then(() => {
          this.$vs.notify({ color: 'success', title: 'Added', text: 'Manual transaction added.' })
          this.manualTx.form.amount = ''
          this.manualTx.form.label = ''
          this.loadManualTx()
          this.refreshAll()
        })
        .catch(() => { this.$vs.notify({ color: 'danger', title: 'Error', text: 'Could not add transaction.' }) })
        .finally(() => { this.manualTx.saving = false })
    },
    removeManualTx(item) {
      this.$store.dispatch('BankList/deleteManualTx', { id: item.id })
        .then(() => {
          this.manualTx.items = this.manualTx.items.filter((i) => i.id !== item.id)
          this.manualTx.count = this.manualTx.items.length
          this.manualTx.total_eur = this.manualTx.items.reduce((s, i) => s + i.amount_eur, 0)
          this.refreshAll()
        })
        .catch(() => { this.$vs.notify({ color: 'danger', title: 'Error', text: 'Could not delete transaction.' }) })
    },
    removeManualFromDetail(item) {
      this.$store.dispatch('BankList/deleteManualTx', { id: item.manual_id })
        .then(() => {
          this.$vs.notify({ color: 'success', title: 'Deleted', text: 'Manual transaction removed.' })
          this.refreshAll()
        })
        .catch(() => { this.$vs.notify({ color: 'danger', title: 'Error', text: 'Could not delete transaction.' }) })
    },

    /* ---------- credit (principal / dobanda) ---------- */
    openCredit(kind) {
      this.credit.kind = kind
      this.credit.active = true
      this.loadCredit()
    },
    loadCredit() {
      this.credit.loading = true
      this.$store.dispatch('BankList/fetchCreditOps', { kind: this.credit.kind, year: this.selectedYear })
        .then((res) => {
          const data = res.data || {}
          this.credit.items = data.items || []
          this.credit.count = data.count || 0
          this.credit.total_eur = data.total_eur || 0
        })
        .catch(() => { this.$vs.notify({ color: 'danger', title: 'Error', text: 'Could not load credit operations.' }) })
        .finally(() => { this.credit.loading = false })
    },
    itemRowClass(item) {
      if (item.is_investment) return 'item-capex'
      if (this.isEjected(item)) return 'item-ejected'
      if (item.category === 'principal') return 'item-principal'
      if (item.category === 'dobanda') return 'item-dobanda'
      return 'item-included'
    },

    /* ---------- eject rules creation ---------- */
    openNewRule() {
      // Manual rule creation from the rules manager (no source operation).
      this.eject.item = null
      this.eject.field = 'comment'
      this.eject.mode = 'contains'
      this.eject.value = ''
      this.eject.note = ''
      this.eject.previewCount = null
      this.eject.active = true
    },
    onEjectCriteriaChange() {
      // debounce to avoid one request per keystroke
      this.eject.previewCount = null
      if (this._previewTimer) clearTimeout(this._previewTimer)
      this._previewTimer = setTimeout(() => { this.previewEject() }, 350)
    },
    previewEject() {
      if (!this.eject.value || !this.eject.value.trim()) { this.eject.previewCount = null; return }
      this.eject.previewing = true
      this.$store.dispatch('BankList/previewEjectRule', { field: this.eject.field, value: this.eject.value, mode: this.eject.mode })
        .then((res) => { this.eject.previewCount = (res.data && res.data.match_count) || 0 })
        .catch(() => { this.eject.previewCount = null })
        .finally(() => { this.eject.previewing = false })
    },
    confirmEject() {
      this.$store.dispatch('BankList/createEjectRule', {
        field: this.eject.field, value: this.eject.value, mode: this.eject.mode, note: this.eject.note
      })
        .then((res) => {
          const n = (res.data && res.data.match_count) || 0
          this.$vs.notify({ color: 'success', title: 'Rule created', text: `${n} past op(s) ejected; future ones will be too.` })
          this.eject.active = false
          this.refreshAll()
          if (this.rules.active) this.reloadRules()
        })
        .catch(() => { this.$vs.notify({ color: 'danger', title: 'Error', text: 'Could not create rule.' }) })
    },

    /* ---------- rules manager ---------- */
    openRules() {
      this.rules.active = true
      this.reloadRules()
    },
    reloadRules() {
      this.rules.loading = true
      this.$store.dispatch('BankList/fetchEjectRules')
        .then((res) => { this.rules.list = res.data || [] })
        .catch(() => { this.$vs.notify({ color: 'danger', title: 'Error', text: 'Could not load rules.' }) })
        .finally(() => { this.rules.loading = false })
    },
    removeRule(r) {
      this.$store.dispatch('BankList/deleteEjectRule', { id: r.id })
        .then(() => {
          this.rules.list = this.rules.list.filter((x) => x.id !== r.id)
          this.refreshAll()
        })
        .catch(() => { this.$vs.notify({ color: 'danger', title: 'Error', text: 'Could not delete rule.' }) })
    },
    openRuleMatches(r) {
      this.ruleMatches.rule = r
      this.ruleMatches.items = []
      this.ruleMatches.count = 0
      this.ruleMatches.total_eur = 0
      this.ruleMatches.active = true
      this.ruleMatches.loading = true
      this.$store.dispatch('BankList/fetchRuleMatches', { id: r.id, year: this.selectedYear })
        .then((res) => {
          const data = res.data || {}
          this.ruleMatches.items = data.items || []
          this.ruleMatches.count = data.count || 0
          this.ruleMatches.total_eur = data.total_eur || 0
        })
        .catch(() => { this.$vs.notify({ color: 'danger', title: 'Error', text: 'Could not load operations.' }) })
        .finally(() => { this.ruleMatches.loading = false })
    },

    /* ---------- helpers ---------- */
    refreshAll() {
      this.loadExpenses()
      if (this.detail.active) this.loadDetail()
      if (this.credit.active) this.loadCredit()
      if (this.investments.active) this.loadInvestments()
    },
    monthLabel(month) { return this.monthNames[month - 1] || '' },
    fieldLabel(field) {
      return field === 'creditor' ? 'Beneficiary' : (field === 'debtor' ? 'Payer' : 'Description')
    },
    formatDate(d) {
      const dt = new Date(d)
      return isNaN(dt) ? d : dt.toLocaleDateString('en-GB')
    },
    formatEur(amount) {
      return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR' }).format(amount || 0)
    },
    formatPct(n) {
      if (n === null || n === undefined) return '—'
      return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) + ' %'
    },
    formatM3(n) {
      if (n === null || n === undefined) return '—'
      return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 }).format(n) + ' m³'
    },
    formatPerM3(n) {
      if (n === null || n === undefined) return '—'
      return this.formatEur(n) + ' /m³'
    },
    resultExploitation(row) {
      return (row.revenue_eur || 0) - (row.expense_eur || 0)
    },
    formatVariation(n) {
      if (n === null || n === undefined) return '—'
      const sign = n > 0 ? '+' : (n < 0 ? '−' : '')
      return sign + this.formatEur(Math.abs(n))
    },
    variationClass(n) {
      if (n === null || n === undefined) return 'text-muted'
      if (n > 0) return 'text-success'
      if (n < 0) return 'text-danger'
      return 'text-muted'
    },
    formatRon(amount) {
      return new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(amount || 0)
    },
    formatNative(amount, currency) {
      try {
        return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency || 'EUR' }).format(amount || 0)
      } catch (e) {
        return `${(amount || 0).toFixed(2)} ${currency || ''}`.trim()
      }
    }
  }
}
</script>

<style scoped>
.text-muted { color: #6c757d; }
.text-danger { color: #ea5455; }

/* ----- Panneaux KPI synthétiques ----- */
.kpi-panel { background: #f8fafc; border: 1px solid #e5e7eb; border-radius: 10px; padding: 18px 20px; }
.kpi-panel-industrial { background: #f5f8f4; border-color: #dbe7d6; }
.kpi-panel-title { font-size: 0.78rem; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #64748b; margin-bottom: 14px; }
.kpi-panel-industrial .kpi-panel-title { color: #4d7c4f; }
.kpi-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 12px; }
.kpi-cell { background: #fff; border: 1px solid #eef2f7; border-radius: 8px; padding: 10px 14px; }
.kpi-cell-strong { border-color: #cbd5e1; box-shadow: 0 1px 3px rgba(15,23,42,.06); }
.kpi-label { font-size: 0.74rem; color: #94a3b8; margin-bottom: 4px; }
.kpi-value { font-size: 1.18rem; font-weight: 700; color: #1e293b; }
.year-select { width: 120px; }
.total-row { border-top: 2px solid #e5e7eb; }
/* TOTAL row inside the table: bold, separated, slightly highlighted */
.total-table-row >>> td { border-top: 2px solid #cbd5e1 !important; background: #f8fafc !important; font-weight: 700; }
.text-right { text-align: right; }
/* vuesax : aligner le TITRE d'en-tête à droite comme les valeurs (le th interne est un flex calé à gauche) */
.text-right >>> .vs-table-text { justify-content: flex-end; }

.detail-popup >>> .vs-popup { width: 1040px; max-width: 96vw; }
.eject-popup >>> .vs-popup { width: 520px; max-width: 95vw; }
.rules-popup >>> .vs-popup { width: 620px; max-width: 95vw; }
.matches-popup >>> .vs-popup { width: 1040px; max-width: 96vw; }
.recap-popup >>> .vs-popup { width: 560px; max-width: 95vw; }

/* expenses recap breakdown */
.recap-body { padding: 4px 2px; }
.recap-row { display: flex; justify-content: space-between; align-items: center; padding: 7px 4px; }
.recap-head { font-size: 1.05rem; border-bottom: 2px solid #e5e7eb; padding-bottom: 10px; margin-bottom: 4px; }
.recap-section { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: #94a3b8; margin-top: 12px; margin-bottom: 2px; }
.recap-subtotal { border-top: 1px dashed #e5e7eb; }
.recap-total { border-top: 2px solid #e5e7eb; margin-top: 8px; padding-top: 12px; }
.recap-dot { display: inline-block; width: 9px; height: 9px; border-radius: 50%; margin-right: 8px; vertical-align: middle; }
.bankcheck-popup >>> .vs-popup { width: 900px; max-width: 96vw; }

.sev-dot { width: 10px; height: 10px; border-radius: 50%; margin-top: 6px; flex-shrink: 0; }
.sev-high { background: #ea5455; }
.sev-medium { background: #ff9f43; }
.sev-low { background: #b8c2cc; }
.iss-high { border-left: 4px solid #ea5455; background: #fff5f5; }
.iss-medium { border-left: 4px solid #ff9f43; background: #fffaf0; }
.iss-low { border-left: 4px solid #b8c2cc; background: #fafafa; }
.badge-high { background: #fee2e2; color: #991b1b; }
.badge-medium { background: #ffedd5; color: #9a3412; }
.badge-low { background: #f1f5f9; color: #475569; }
.bg-green-50 { background: #f0fdf4; }
.bg-orange-50 { background: #fff7ed; }
.text-success { color: #28c76f; }
.text-warning { color: #ff9f43; }
.text-primary { color: #7367f0; }

.detail-item { background: #fff; border-color: #e5e7eb; }
/* counted (accounted) = vert, bien lisible / ejected = rouge clair / credit = bleu */
.item-included { border-left: 4px solid #28c76f; background: #effdf3; }
.item-ejected { border-left: 4px solid #ea5455; background: #fff1f2; }
.item-capex { border-left: 4px solid #7367f0; background: #f5f4ff; }
.badge-capex { background: #e0e7ff; color: #4338ca; }
.item-principal, .item-dobanda { border-left: 4px solid #3b82f6; background: #eff6ff; }
.badge-principal, .badge-dobanda { background: #dbeafe; color: #1e40af; }

/* credit accent color (principal + dobanda) = bleu */
.col-principal, .col-dobanda { color: #2563eb; }
/* ----- Panneau (i) Informations ----- */
.info-panel { border: 1px solid #e5e7eb; border-radius: 10px; background: #fbfcfe; }
.info-head { display: flex; align-items: center; padding: 12px 16px; cursor: pointer; color: #475569; user-select: none; }
.info-head:hover { color: #1e293b; }
.info-title { font-size: 0.78rem; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; }
.info-body { padding: 0 20px 18px; font-size: 0.82rem; line-height: 1.55; color: #475569; }
.info-callout { background: #fff7ed; border: 1px solid #fed7aa; border-radius: 8px; padding: 12px 16px; margin-bottom: 18px; color: #7c4a12; }
.info-callout ul { margin: 8px 0 8px 18px; list-style: disc; }
.info-callout li { margin-bottom: 6px; }
.info-sec { margin-bottom: 18px; }
.info-sec-title { font-weight: 700; color: #1e293b; margin-bottom: 6px; }
.info-sec ul { margin-left: 18px; list-style: disc; }
.info-sec ol { margin-left: 18px; list-style: decimal; }
.info-sec li { margin-bottom: 5px; }
.info-p { margin-bottom: 6px; }
.info-formulas li { font-variant-numeric: tabular-nums; }
.info-body code { background: #eef2f7; border-radius: 3px; padding: 0 4px; font-size: 0.95em; }

.col-link { cursor: pointer; text-decoration: none; border-bottom: 1px dashed currentColor; }
.col-link:hover { opacity: 0.75; }
.total-expenses { border-left: 1px solid #e5e7eb; }
.total-revenue { border-left: 1px solid #e5e7eb; padding-left: 2rem; }

.comment-input { width: 320px; max-width: 60%; }

.badge { font-size: 0.7rem; font-weight: 700; padding: 2px 8px; border-radius: 10px; }
.badge-in { background: #dcfce7; color: #166534; }
.badge-manual { background: #e2e8f0; color: #334155; }
.manual-form { background: #f8fafc; border: 1px solid #e5e7eb; }
.badge-out { background: #fee2e2; color: #991b1b; }
.badge-rule { background: #fef3c7; color: #92400e; }
.badge-account { background: #e0e7ff; color: #3730a3; font-weight: 600; }

.preview-box { background: #f1f5f9; border: 1px dashed #cbd5e1; }
.bg-gray-50 { background: #f8f9fa; }
.gap-1 { gap: 0.25rem; }
</style>

<!-- Non-scoped: keep nested popups above the Eject-rules popup -->
<style>
.con-vs-popup.matches-popup { z-index: 53200 !important; }
.con-vs-popup.eject-popup { z-index: 53300 !important; }

.cashflow-actions-menu .vs-dropdown--item a,
.cashflow-actions-menu .vs-dropdown--item { white-space: normal; }
.cashflow-actions-menu .vs-dropdown--item a { padding-top: 8px; padding-bottom: 8px; line-height: 1.2; }
.cashflow-actions-menu .dd-title { font-weight: 600; line-height: 1.15; }
.cashflow-actions-menu .dd-sub { font-size: 11px; opacity: .6; white-space: normal; max-width: 260px; }
</style>
